/*
 * Duroflex -> MoEngage bridge.  (2026-08-18)
 *
 * Single place the storefront talks to MoEngage. The MoEngage Web SDK is
 * already on every page via the MoEngage Shopify app embed
 * (app_id PAUVTHMVA1H1ZIUT9SZRCQLO, cluster DC_3), so this file only has to
 * translate a "transition" payload into SDK calls.
 *
 * Why the SDK and not the S2S POST /v1/transition/<app_id> endpoint: that
 * endpoint answers a browser CORS preflight with 403 "Invalid CORS request",
 * so it cannot be called from theme JS at all. Its Authorization: Basic
 * credential would also be readable in View Source, letting anyone write into
 * the workspace. The SDK lands the identical attribute keys
 * (u_fn / u_ln / u_em / u_mb plus the qlp_* and sq_* customs) and the identical
 * event names in the same MoEngage account, with no credential shipped.
 *
 * The transition payload's `platform` and `current_time` are supplied by the
 * SDK itself and are therefore not passed through.
 */
(function (w) {
  'use strict';
  if (w.DFXMoEngage) return;

  // The SDK is loaded async by the app embed, so a shopper can verify an OTP
  // before window.Moengage exists. Queue instead of dropping the event.
  var queue = [];
  var READY_POLL_MS = 250;
  var READY_TIMEOUT_MS = 15000;
  var waiting = false;

  function sdk() {
    return (w.Moengage && typeof w.Moengage.track_event === 'function') ? w.Moengage : null;
  }

  function flush() {
    var m = sdk();
    if (!m) return false;
    while (queue.length) {
      var job = queue.shift();
      try { job(m); } catch (e) { log('job failed', e); }
    }
    return true;
  }

  function run(job) {
    var m = sdk();
    if (m) { try { job(m); } catch (e) { log('job failed', e); } return; }
    queue.push(job);
    if (waiting) return;
    waiting = true;
    var waited = 0;
    var t = setInterval(function () {
      waited += READY_POLL_MS;
      if (flush()) { clearInterval(t); waiting = false; return; }
      if (waited >= READY_TIMEOUT_MS) {
        clearInterval(t); waiting = false;
        log('SDK never became available; dropped ' + queue.length + ' event(s)');
        queue.length = 0;
      }
    }, READY_POLL_MS);
  }

  function log() {
    if (!w.DFXMoEngage || !w.DFXMoEngage.debug) return;
    try { console.log.apply(console, ['[dfx-moengage]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  // MoEngage keys every profile off the E.164 phone, matching the customer_id
  // in the transition payloads. Same normaliser shape the OTP service uses.
  function e164(phone) {
    var s = String(phone === null || phone === undefined ? '' : phone).replace(/[^\d+]/g, '');
    if (!s) return '';
    if (s.charAt(0) === '+') return s;
    s = s.replace(/\D/g, '');
    // 00 is the international access prefix, so 0091… is the same number as +91….
    // Handles any country code, not just 91.
    if (s.length > 2 && s.slice(0, 2) === '00') return '+' + s.slice(2);
    if (s.length === 10) return '+91' + s;
    if (s.length === 11 && s.charAt(0) === '0') return '+91' + s.slice(1);
    if (s.length === 12 && s.slice(0, 2) === '91') return '+' + s;
    return '+' + s;
  }

  // Reserved MoEngage attributes have dedicated setters; everything else is a
  // custom attribute. Writing u_fn via add_user_attribute would create a
  // separate custom field rather than filling the reserved one.
  var RESERVED = {
    u_fn: 'add_first_name',
    u_ln: 'add_last_name',
    u_em: 'add_email',
    u_mb: 'add_mobile',
    u_gd: 'add_gender',
    u_bd: 'add_birthday'
  };

  function isEmpty(v) {
    return v === null || v === undefined || v === '';
  }

  /*
   * identify(customerId, attributes)
   * Mirrors the {"type":"customer"} element of a transition payload.
   *
   * Empty values are skipped rather than written. Writing '' would blank the
   * field on an existing MoEngage profile — a lead who gave no email would wipe
   * the address a previous order had captured, and a shopper who verified before
   * entering a pincode would erase the pincode from a prior visit. Non-empty
   * values, including the custom qlp_ and sq_ keys, pass through exactly as given.
   */
  function identify(customerId, attributes) {
    var id = e164(customerId);
    if (!id) { log('identify skipped: no customer id'); return; }
    var attrs = attributes || {};
    run(function (m) {
      m.add_unique_user_id(id);
      Object.keys(attrs).forEach(function (key) {
        var val = attrs[key];
        if (isEmpty(val)) return;
        var setter = RESERVED[key];
        if (setter && typeof m[setter] === 'function') { m[setter](val); return; }
        m.add_user_attribute(key, val);
      });
      log('identify', id, attrs);
    });
  }

  /*
   * track(action, attributes)
   * Mirrors one entry of the {"type":"event"} element's actions array.
   */
  function track(action, attributes) {
    if (!action) return;
    var attrs = attributes || {};
    run(function (m) {
      m.track_event(action, attrs);
      log('track', action, attrs);
    });
  }

  /*
   * THE ONLY FIELDS DUROFLEX SENDS TO MOENGAGE (2026-08-20).
   *
   * Every event still fires under its own name; only the attributes are capped.
   * Both payloads carry the same five values:
   *   mobile  (+91…)  id (same value)  pincode  name  email
   *
   * leadProfile()  -> the {"type":"customer"} attributes. Uses MoEngage's
   *                   reserved keys so the values land in the real profile
   *                   fields: u_mb via add_mobile, u_fn via add_first_name,
   *                   u_em via add_email. `id` is not a key here — it is the
   *                   unique user id, set by emit()/identify() from the phone.
   * leadEventAttrs() -> the event attributes, using plain names.
   *
   * Empty values are omitted (identify() skips them anyway) so a shopper who
   * gave no email never blanks an address captured by a previous order.
   */
  function leadProfile(o) {
    o = o || {};
    var id = e164(o.phone);
    var out = {};
    if (id) out.u_mb = id;
    if (o.pincode) out.pincode = String(o.pincode).trim();
    if (o.name) out.u_fn = String(o.name).trim();
    if (o.email) out.u_em = String(o.email).trim();
    return out;
  }

  function leadEventAttrs(o) {
    o = o || {};
    var id = e164(o.phone);
    var out = {};
    if (id) { out.mobile = id; out.id = id; }
    if (o.pincode) out.pincode = String(o.pincode).trim();
    if (o.name) out.name = String(o.name).trim();
    if (o.email) out.email = String(o.email).trim();
    return out;
  }

  /*
   * emit(customerId, userAttributes, action, eventAttributes)
   * One transition payload in one call: sets the profile, then fires the event.
   */
  function emit(customerId, userAttributes, action, eventAttributes) {
    identify(customerId, userAttributes);
    track(action, eventAttributes);
  }

  // Device/browser/OS strings for the shared event attributes. Kept crude on
  // purpose: MoEngage only needs a coarse bucket, and UA sniffing is fragile.
  function deviceType() {
    var ua = navigator.userAgent || '';
    if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
    if (/Mobi|Android|iPhone|iPod|IEMobile|Opera Mini/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  function browser() {
    var ua = navigator.userAgent || '';
    if (/Edg\//.test(ua)) return 'Edge';
    if (/OPR\/|Opera/.test(ua)) return 'Opera';
    if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return 'Chrome';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Safari\//.test(ua)) return 'Safari';
    return 'Other';
  }

  function os() {
    var ua = navigator.userAgent || '';
    if (/Android/i.test(ua)) return 'Android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac OS X/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return 'Other';
  }

  function param(name) {
    try { return new URLSearchParams(location.search).get(name) || ''; }
    catch (e) { return ''; }
  }

  // utm_* survive the landing page via the popup's own attribution store when
  // it is present; otherwise fall back to the current query string.
  function attribution() {
    var stored = {};
    try {
      var D = w.DuroflexPopup || w.DFXPopup;
      if (D && typeof D.getAttribution === 'function') stored = D.getAttribution() || {};
    } catch (e) {}
    return {
      utm_source: stored.utm_source || param('utm_source') || '',
      utm_medium: stored.utm_medium || param('utm_medium') || '',
      utm_campaign: stored.utm_campaign || param('utm_campaign') || ''
    };
  }

  /* Attributes shared by every Duroflex lead event.
     NO LONGER CALLED as of 2026-08-20 — event attributes are capped at the five
     fields above. Kept so the source/device/UTM shape is one edit away if the
     wider payload is ever wanted back. */
  function baseEventAttrs() {
    var a = attribution();
    return {
      source: 'shopify',
      device_type: deviceType(),
      browser: browser(),
      os: os(),
      url: location.href,
      utm_source: a.utm_source,
      utm_medium: a.utm_medium,
      utm_campaign: a.utm_campaign
    };
  }

  function merge() {
    var out = {};
    for (var i = 0; i < arguments.length; i++) {
      var src = arguments[i] || {};
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) out[k] = src[k];
    }
    return out;
  }

  w.DFXMoEngage = {
    debug: false,
    e164: e164,
    identify: identify,
    track: track,
    emit: emit,
    leadProfile: leadProfile,
    leadEventAttrs: leadEventAttrs,
    baseEventAttrs: baseEventAttrs,   // retained, no longer used
    merge: merge,
    available: function () { return !!sdk(); }
  };
})(window);
